/*
  /privacy — Sentio Mind privacy policy.

  Content only. Blocks are tuples so the renderer in LegalPage.jsx stays a
  single switch and the document reads as a document in source too:
    ["p",    text]              paragraph
    ["h",    text]              sub-heading inside a section
    ["ul",   [items]]           bulleted list
    ["note", text]              highlighted callout
    ["dl",   [[label, value]]]  contact / address card
*/

export const PRIVACY_EFFECTIVE = "4 September 2026";
export const PRIVACY_UPDATED = "25 September 2026";

const CONTACT_EMAIL = "support@sentiomind.in";
const HEAD_OFFICE = "1111, Y Block, Kidwai Nagar,\nKanpur, Uttar Pradesh – 208011";

export const privacyDoc = {
  slug: "privacy",
  eyebrow: "Legal · Privacy",
  title: "Privacy Policy",
  titleAccent: "SGRG Technologies Private Limited — “Sentio Mind”",
  sub:
    "How we collect, process, use, store, protect and share personal data, and the rights available to you — written to operate in accordance with the Digital Personal Data Protection Act, 2023.",
  effective: PRIVACY_EFFECTIVE,
  updated: PRIVACY_UPDATED,

  intro: [
    "SGRG Technologies Private Limited (“Company”, “we”, “us” or “our”), the owner and operator of the brand “Sentio Mind”, is committed to protecting the privacy and security of personal data processed through its website, platform, software, products and services.",
    "Sentio Mind is an AI-powered behaviour intelligence platform that uses existing camera infrastructure and machine-learning technologies to generate real-time insights relating to engagement, attention, participation, emotional signals, behavioural patterns and risk indicators for institutions.",
    "This Privacy Policy explains how we collect, process, use, store, protect and share personal data and the rights available to individuals in relation to such data. This Policy is intended to operate in accordance with the Digital Personal Data Protection Act, 2023 (“DPDP Act”), the applicable rules and regulations made thereunder, and other applicable laws.",
  ],

  sections: [
    {
      id: "about",
      num: 1,
      title: "About Sentio Mind",
      blocks: [
        ["p", "Sentio Mind is a brand of SGRG Technologies Private Limited, CIN: U62090UP2025PTC240171."],
        ["p", "Sentio Mind provides AI-powered behaviour and emotion intelligence solutions to organizations and institutions, including institutions in sectors such as:"],
        ["ul", [
          "Education;",
          "Corporate and workplaces;",
          "Healthcare;",
          "Public infrastructure; and",
          "Other institutional environments.",
        ]],
        ["p", "The platform is designed to convert existing camera infrastructure into actionable behavioural intelligence without requiring additional hardware or intrusive monitoring mechanisms."],
        ["p", "Our approach is based on privacy by design, data minimization, security and responsible use of AI."],
      ],
    },
    {
      id: "our-role",
      num: 2,
      title: "Our Role Under Data Protection Law",
      blocks: [
        ["p", "Depending on the nature of the services provided and the arrangement with the customer institution, the Company may act either as a Data Fiduciary or as a Data Processor."],
        ["ul", [
          "Where the Company determines the purpose and means of processing personal data, it may act as a Data Fiduciary.",
          "Where the Company processes personal data solely on behalf of an institution or customer and according to its documented instructions, the Company may act as a Data Processor.",
        ]],
        ["p", "Where Sentio Mind is deployed by an institution, individuals should also review the privacy notice or data protection policy of that institution, as the institution may determine the purposes for which certain personal data is collected and processed."],
      ],
    },
    {
      id: "data-we-process",
      num: 3,
      title: "Personal Data We May Process",
      blocks: [
        ["p", "Depending upon the deployment, configuration and services used, Sentio Mind may process the following categories of information:"],

        ["h", "A. Camera and visual data"],
        ["p", "Where camera infrastructure is connected to Sentio Mind, the system may receive visual information from the relevant camera feed for the purpose of AI-based processing and generation of behavioural insights."],
        ["p", "Sentio Mind is designed with a privacy-first architecture and aims to minimize the collection and retention of raw camera data. Depending on the deployment configuration and technical requirements, camera data may be processed to generate the intended analytical outputs. The Company does not retain raw camera footage beyond the period and purposes necessary for providing the applicable services, except where otherwise agreed with the customer or required by law."],

        ["h", "B. Behavioural and engagement information"],
        ["p", "The platform may generate analytical information such as:"],
        ["ul", [
          "Engagement indicators;",
          "Attention indicators;",
          "Participation indicators;",
          "Focus-related indicators;",
          "Behavioural patterns;",
          "Behavioural anomalies;",
          "Disengagement indicators;",
          "Risk indicators; and",
          "Other metrics configured for the relevant institutional deployment.",
        ]],

        ["h", "C. Emotional and affective signals"],
        ["p", "Depending on the features enabled, the platform may analyse visual patterns, facial expressions, micro-expressions or other observable signals to generate emotional or affective indicators."],
        ["p", "Such indicators are generated by AI models and should be understood as analytical signals or predictions rather than definitive statements about an individual’s actual emotional or mental state."],

        ["h", "D. Account and contact information"],
        ["p", "Where applicable, we may collect:"],
        ["ul", [
          "Name;",
          "Email address;",
          "Telephone number;",
          "Designation;",
          "Organization/institution details;",
          "Login credentials or authentication information; and",
          "Other information voluntarily provided while contacting or using our services.",
        ]],

        ["h", "E. Technical information"],
        ["p", "We may process information required to operate, secure and maintain the platform, such as:"],
        ["ul", [
          "Device information;",
          "Browser information;",
          "IP address;",
          "Login and access records;",
          "System logs;",
          "Date and time of access;",
          "Platform usage information; and",
          "Security and diagnostic information.",
        ]],

        ["h", "F. Information provided by institutions"],
        ["p", "Where Sentio Mind is deployed by an institution, the Company may receive information from that institution relating to authorized users, organizational structures, classrooms, departments, teams or other institutional configurations."],
      ],
    },
    {
      id: "not-collected",
      num: 4,
      title: "What We Do Not Seek to Collect or Retain",
      blocks: [
        ["p", "Sentio Mind is designed around data minimization."],
        ["p", "Unless specifically required for an agreed feature, lawful purpose or institutional deployment, we do not seek to collect unnecessary personal information."],
        ["p", "The platform is designed so that raw camera footage is not retained as a routine data store, and processed metrics are intended to be protected through appropriate technical safeguards."],
        ["p", "Where the system can achieve its intended purpose through aggregated, anonymized or otherwise privacy-preserving information, we seek to use such information instead of identifiable personal data."],
      ],
    },
    {
      id: "purposes",
      num: 5,
      title: "Purposes for Which We Process Data",
      blocks: [
        ["p", "Personal data may be processed for the following purposes:"],
        ["ul", [
          "To provide and operate the Sentio Mind platform;",
          "To process authorized camera feeds and generate behavioural intelligence;",
          "To generate engagement, attention, participation and other analytical insights;",
          "To identify behavioural anomalies and risk indicators;",
          "To support early intervention and institutional decision-making;",
          "To improve safety, learning, productivity, engagement and well-being;",
          "To provide dashboards, reports and analytics to authorized institutional users;",
          "To authenticate users and maintain account security;",
          "To provide customer support and respond to enquiries;",
          "To maintain, troubleshoot and improve the platform;",
          "To detect, prevent and investigate security incidents, fraud, misuse or unauthorized access;",
          "To comply with applicable legal and regulatory requirements;",
          "To enforce contractual rights and protect our legal interests;",
          "To develop, test and improve our products and technologies, subject to applicable law and appropriate safeguards;",
          "To generate aggregated or anonymized statistics, benchmarking information and analytics; and",
          "For any other purpose expressly communicated to the individual and permitted under applicable law.",
        ]],
        ["p", "We do not use personal data for a purpose that is incompatible with the purpose for which it was collected, except where otherwise permitted by applicable law."],
      ],
    },
    {
      id: "legal-basis",
      num: 6,
      title: "Legal Basis for Processing",
      blocks: [
        ["p", "Under the DPDP Act, personal data may be processed on the basis of consent or for specified legitimate uses permitted by law."],
        ["p", "Where consent is required, we will seek consent in a manner that is:"],
        ["ul", ["Free;", "Specific;", "Informed;", "Unconditional; and", "Unambiguous."]],
        ["p", "The notice provided at the time of collection or processing will explain the personal data being processed and the purpose for which it is being processed."],
        ["p", "Where Sentio Mind processes personal data on behalf of an institution, the relevant institution may be responsible for determining the appropriate legal basis and obtaining any required consent or authorization."],
      ],
    },
    {
      id: "camera-processing",
      num: 7,
      title: "Camera-Based Processing",
      blocks: [
        ["p", "Sentio Mind may process video or visual information obtained from existing camera infrastructure connected to the platform."],
        ["p", "The purpose of such processing is to identify and analyse observable patterns and generate actionable institutional insights."],
        ["note", "Sentio Mind is not intended to function as a conventional CCTV surveillance or video-recording platform."],
        ["p", "The system is designed to process camera data through AI models and generate analytical outputs rather than maintain a repository of raw video footage."],
        ["p", "Where technically feasible, raw visual information is processed in a manner designed to minimize retention and unnecessary access."],
        ["p", "Institutions deploying Sentio Mind are responsible for ensuring that cameras are deployed lawfully and that individuals are appropriately informed about the processing taking place within their premises."],
      ],
    },
    {
      id: "emotion-analytics",
      num: 8,
      title: "Emotion and Behaviour Analytics",
      blocks: [
        ["p", "Sentio Mind may use machine learning, computer vision and behavioural science techniques to generate analytical indicators."],
        ["p", "These may include indicators relating to:"],
        ["ul", [
          "Engagement;",
          "Attention;",
          "Focus;",
          "Participation;",
          "Behavioural patterns;",
          "Emotional signals;",
          "Disengagement;",
          "Distress-related indicators; and",
          "Other risk or anomaly indicators.",
        ]],
        ["note", "Such outputs are AI-generated inferences or indicators and are not medical, psychological or clinical diagnoses. AI-generated insights should not be treated as a substitute for professional medical, psychological, educational, HR or other expert judgment."],
        ["p", "Institutions and authorized users remain responsible for interpreting the information appropriately and taking decisions through appropriate human oversight."],
      ],
    },
    {
      id: "responsible-ai",
      num: 9,
      title: "Responsible Use of AI",
      blocks: [
        ["p", "Sentio Mind is designed to support human decision-making and not to replace it."],
        ["p", "AI-generated outputs may not always be accurate and may be affected by factors including lighting, camera positioning, image quality, environmental conditions, individual differences and limitations inherent in machine-learning models."],
        ["p", "Accordingly:"],
        ["ul", [
          "AI outputs should be treated as indicators rather than absolute facts;",
          "Human judgment should be applied before taking material action based on an AI-generated insight;",
          "The platform should not be used as the sole basis for decisions having significant consequences for an individual;",
          "Institutions should establish appropriate internal procedures for reviewing and acting upon AI-generated alerts; and",
          "Sentio Mind may update or improve its models from time to time to enhance accuracy, safety and performance.",
        ]],
      ],
    },
    {
      id: "data-sharing",
      num: 10,
      title: "Data Sharing",
      blocks: [
        ["note", "Sentio Mind does not sell personal data."],
        ["p", "Personal data may be shared only where necessary and legally permitted, including in the following circumstances:"],

        ["h", "A. Client institutions"],
        ["p", "Where Sentio Mind is deployed by an institution, relevant analytics and authorized information may be made available to the institution and its authorized personnel in accordance with the applicable contractual arrangement and legal basis."],

        ["h", "B. Service providers and technology partners"],
        ["p", "We may engage third-party service providers to support activities such as:"],
        ["ul", [
          "Cloud infrastructure;",
          "Data hosting and storage;",
          "Cybersecurity;",
          "Software infrastructure;",
          "Technical maintenance;",
          "Analytics;",
          "Customer support; and",
          "Other services necessary for operating the platform.",
        ]],
        ["p", "Such parties will be permitted to process personal data only to the extent necessary to provide the relevant services and subject to appropriate contractual and security safeguards."],

        ["h", "C. Legal and regulatory requirements"],
        ["p", "We may disclose personal data where required or permitted by applicable law, regulation, court order, governmental authority or lawful request."],

        ["h", "D. Protection of rights and safety"],
        ["p", "Information may be disclosed where reasonably necessary to:"],
        ["ul", [
          "Prevent fraud or misuse;",
          "Investigate security incidents;",
          "Protect the rights, property or safety of the Company, users or other persons; or",
          "Respond to an emergency or serious risk where permitted by law.",
        ]],

        ["h", "E. Business transactions"],
        ["p", "If the Company undergoes a merger, acquisition, restructuring, sale of assets or similar corporate transaction, relevant information may be transferred as part of that transaction, subject to applicable law and appropriate confidentiality and security safeguards."],
      ],
    },
    {
      id: "aggregated",
      num: 11,
      title: "Aggregated and Anonymized Information",
      blocks: [
        ["p", "Where possible, we may convert personal data into aggregated or anonymized information so that individuals cannot reasonably be identified."],
        ["p", "Such information may be used for:"],
        ["ul", [
          "Product improvement;",
          "Performance analysis;",
          "Benchmarking;",
          "Research and development;",
          "Business analytics;",
          "Demonstrations; and",
          "Marketing or communications.",
        ]],
        ["p", "We will not intentionally use anonymized information to re-identify individuals."],
      ],
    },
    {
      id: "security",
      num: 12,
      title: "Data Security",
      blocks: [
        ["p", "We implement reasonable technical and organizational measures designed to protect personal data from:"],
        ["ul", [
          "Unauthorized access;",
          "Unauthorized disclosure;",
          "Loss;",
          "Destruction;",
          "Alteration;",
          "Misuse; and",
          "Other security risks.",
        ]],
        ["p", "Depending on the deployment, security measures may include:"],
        ["ul", [
          "Encryption of data in transit and at rest;",
          "Role-based access controls;",
          "Authentication mechanisms;",
          "Restricted administrative access;",
          "System monitoring and logging;",
          "Secure software development practices;",
          "Access controls based on business necessity; and",
          "Periodic review of security measures.",
        ]],
        ["p", "No electronic system can be guaranteed to be completely secure. Accordingly, while we take reasonable measures to protect personal data, absolute security cannot be guaranteed."],
      ],
    },
    {
      id: "retention",
      num: 13,
      title: "Data Retention",
      blocks: [
        ["p", "We retain personal data only for as long as reasonably necessary to fulfil the purpose for which it was collected or processed, to comply with applicable law, or to fulfil contractual and legitimate business requirements."],
        ["p", "For institutional deployments, the applicable retention period may be determined by the agreement between Sentio Mind and the institution."],
        ["p", "Where personal data is no longer required, we will seek to securely delete, destroy or anonymize it, subject to applicable legal or contractual retention requirements."],
        ["p", "The Company does not intend to retain raw camera footage as a routine long-term data store where the platform configuration permits processing without such retention."],
      ],
    },
    {
      id: "ownership",
      num: 14,
      title: "Data Ownership and Institutional Control",
      blocks: [
        ["p", "For institutional deployments, the rights and responsibilities relating to data may be governed by the agreement between the institution and Sentio Mind."],
        ["p", "Sentio Mind’s platform is designed to support institutional data sovereignty, including controlled access, authorized processing and privacy-preserving deployment."],
        ["p", "Where the institution retains control over the personal data, Sentio Mind will process such data in accordance with the institution’s lawful instructions and the applicable contractual terms."],
        ["p", "On-premise deployment may also be made available for appropriate institutional requirements."],
      ],
    },
    {
      id: "rights",
      num: 15,
      title: "Data Principal Rights",
      blocks: [
        ["p", "Subject to the DPDP Act and other applicable laws, individuals may have rights including:"],
        ["ul", [
          "The right to obtain information about their personal data and its processing;",
          "The right to request correction or updating of inaccurate or incomplete personal data;",
          "The right to request removal of personal data where applicable;",
          "The right to withdraw consent where processing is based on consent;",
          "The right to seek grievance redressal; and",
          "The right to nominate another individual to exercise rights in accordance with applicable law.",
        ]],
        ["p", "Requests may be submitted using the contact details provided in this Policy."],
        ["p", "Where required, we may verify the identity of the person making the request before acting on it."],
        ["p", "Certain requests may be subject to legal, contractual, security or other lawful limitations."],
      ],
    },
    {
      id: "withdrawal",
      num: 16,
      title: "Withdrawal of Consent and Opt-Out",
      blocks: [
        ["p", "Where processing is based on consent, individuals may withdraw consent in accordance with applicable law."],
        ["p", "Sentio Mind supports opt-out mechanisms for applicable deployments, and where an institution has enabled such functionality, an individual who has opted out will be excluded from the relevant processing to the extent technically and legally feasible."],
        ["p", "Withdrawal of consent does not affect the lawfulness of processing carried out before withdrawal."],
        ["p", "Where Sentio Mind acts as a Data Processor for an institution, requests relating to consent or withdrawal may need to be directed to the relevant institution, depending on the contractual and operational arrangement."],
      ],
    },
    {
      id: "children",
      num: 17,
      title: "Children’s Personal Data",
      blocks: [
        ["p", "Sentio Mind may be deployed in educational environments where children may be present."],
        ["p", "Where personal data of a child is processed, the Company will comply with the requirements applicable to children’s personal data under the DPDP Act and applicable rules."],
        ["p", "Where required, verifiable parental consent will be obtained through the appropriate Data Fiduciary or institutional mechanism."],
        ["p", "Sentio Mind will not knowingly undertake processing of children’s personal data in a manner prohibited by applicable law."],
        ["p", "The Company will also implement appropriate safeguards for deployments involving children and will not use children’s data for purposes prohibited under applicable law."],
        ["p", "Educational institutions deploying Sentio Mind remain responsible for ensuring that appropriate notices, permissions, consents and institutional safeguards are implemented."],
      ],
    },
    {
      id: "third-party",
      num: 18,
      title: "Third-Party Websites and Services",
      blocks: [
        ["p", "The Sentio Mind website or platform may contain links to third-party websites, services or resources."],
        ["p", "Those third parties operate under their own privacy policies and terms. Sentio Mind does not control the privacy practices of third-party websites and is not responsible for their practices."],
        ["p", "Users should review the applicable privacy policies before providing personal information to third parties."],
      ],
    },
    {
      id: "google-user-data",
      num: 19,
      title: "Sign in with Google and Google User Data",
      blocks: [
        ["p", "If you choose to sign in to the Sentio Mind app with Google, we receive your name, email address and profile picture from your Google Account. We use them only to create and secure your Sentio Mind account and to show your name in the app."],
        ["p", "When you book or join a session, your email address is added to that session’s Google Calendar invitation, created on Sentio Mind’s own Google account, so that you can join the Google Meet call. We do not read your own Google Calendar or any other data in your Google Account."],
        ["p", "We do not use Google user data for advertising, we do not sell it, and we share it only with the service providers that host and operate Sentio Mind on our behalf."],
        ["note", "Sentio Mind’s use and transfer of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements."],
        ["p", "You can remove Sentio Mind’s access at any time in your Google Account under Security, Third-party apps and services, and you can delete your data by deleting your Sentio Mind account in the app."],
      ],
    },
    {
      id: "cookies",
      num: 20,
      title: "Cookies and Website Technologies",
      blocks: [
        ["p", "The Sentio Mind website may use cookies or similar technologies where required for:"],
        ["ul", [
          "Website functionality;",
          "Security;",
          "Performance;",
          "Analytics; and",
          "Improving user experience.",
        ]],
        ["p", "Where cookies or similar technologies involve personal data or require consent under applicable law, appropriate notices or consent mechanisms will be provided."],
        ["p", "The Company may update its cookie practices as the website and services evolve."],
      ],
    },
    {
      id: "transfers",
      num: 21,
      title: "International Data Transfers",
      blocks: [
        ["p", "Depending on the infrastructure, technology providers and deployment configuration, personal data may be processed or stored outside India."],
        ["p", "Where personal data is transferred outside India, the Company will comply with applicable restrictions, directions and requirements under the DPDP Act, applicable rules and other relevant laws."],
        ["p", "Only information reasonably necessary for the relevant purpose will be transferred, and appropriate contractual, technical and organizational safeguards will be implemented where required."],
      ],
    },
    {
      id: "breaches",
      num: 22,
      title: "Personal Data Breaches",
      blocks: [
        ["p", "We maintain procedures intended to detect, respond to and mitigate personal data breaches."],
        ["p", "In the event of a personal data breach, the Company will take reasonable steps to:"],
        ["ul", [
          "Assess and contain the incident;",
          "Secure affected systems;",
          "Investigate the nature and extent of the breach;",
          "Take remedial measures;",
          "Notify the appropriate authorities where required; and",
          "Notify affected Data Principals where required under applicable law.",
        ]],
        ["p", "Notifications will contain information required under applicable law and will be communicated through appropriate channels."],
      ],
    },
    {
      id: "grievance",
      num: 23,
      title: "Grievance Redressal",
      blocks: [
        ["p", "If you have any concern regarding the processing of your personal data, this Privacy Policy, or the use of Sentio Mind services, you may contact us using the details below."],
        ["dl", [
          ["Grievance officer", "Grievance Officer / Privacy Contact"],
          ["Company", "SGRG Technologies Private Limited"],
          ["Brand", "Sentio Mind"],
          ["CIN", "U62090UP2025PTC240171"],
          ["Email", CONTACT_EMAIL],
          ["Address", HEAD_OFFICE],
        ]],
        ["p", "Where Sentio Mind is processing data solely on behalf of an institution, the individual may also contact the relevant institution’s designated privacy or grievance contact."],
        ["p", "We will review and address grievances in accordance with applicable law and our internal grievance redressal procedures."],
      ],
    },
    {
      id: "changes",
      num: 24,
      title: "Changes to This Privacy Policy",
      blocks: [
        ["p", "We may update this Privacy Policy from time to time to reflect changes in:"],
        ["ul", [
          "Our products and services;",
          "Technology and AI systems;",
          "Data processing practices;",
          "Applicable laws and regulations; or",
          "Our privacy and security practices.",
        ]],
        ["p", "The updated version will be published on the Sentio Mind website with the revised “Last Updated” date."],
        ["p", "Where required by applicable law, we will provide additional notice or obtain consent before implementing material changes to processing practices."],
      ],
    },
    {
      id: "contact",
      num: 25,
      title: "Contact Information",
      blocks: [
        ["p", "For questions, requests or concerns relating to privacy or personal data processing, please contact:"],
        ["dl", [
          ["Company", "SGRG Technologies Private Limited"],
          ["Brand", "Sentio Mind"],
          ["CIN", "U62090UP2025PTC240171"],
          ["Head office", HEAD_OFFICE],
          ["Email", CONTACT_EMAIL],
          ["Website", "www.sentiomind.in"],
        ]],
        ["p", `Last Updated: ${PRIVACY_UPDATED}`],
      ],
    },
  ],

  closing: {
    title: "Our Privacy Commitment",
    paras: [
      "Sentio Mind is built around the principle that technology should provide actionable intelligence without compromising individual privacy.",
      "Accordingly, our platform is designed to:",
    ],
    items: [
      "Minimize the collection and retention of personal data;",
      "Avoid routine retention of raw camera footage;",
      "Protect processed information through appropriate security measures;",
      "Provide institutional control over data;",
      "Support opt-out mechanisms where applicable;",
      "Prefer aggregated and anonymized information wherever reasonably possible;",
      "Restrict access to authorized persons; and",
      "Keep human judgment at the centre of decisions based on AI-generated insights.",
    ],
  },

  crosslink: {
    text: "Looking for the rules that govern your use of the Sentio Mind app and platform?",
    label: "Read the Terms of Service",
    to: "/terms",
  },
};

export default privacyDoc;
